import {
  TRANSLATIONS_UPDATE_LOCALES,
  TRANSLATIONS_UPDATE_TRANSLATIONS,
  TRANSLATIONS_ADD_TRANSLATIONS,
  TRANSLATIONS_UPDATE_FILTER_GROUP,
  TRANSLATIONS_UPDATE_FILTER_KEY,
  TRANSLATIONS_UPDATE_FILTER_LOCALE,
  TRANSLATIONS_UPDATE_FILTER_NAMESPACE,
  TRANSLATIONS_UPDATE_FILTER_VALUE
} from '@/store/mutypes';
import { Translation, TranslationState, RootState } from '@/types';
import { ActionContext } from 'vuex';
import { translationSvc } from '@/services';
import { uniq } from 'lodash';

// initial state
const modState: TranslationState = {
  translations: [],
  locales: [],
  filterNamespace: '',
  filterGroup: '',
  filterLocale: '',
  filterKey: '',
  filterValue: ''
};

// getters
const getters = {
  activeLocales(state: TranslationState): string[] {
    const locales = new Set();
    for (const translation of state.translations) {
      locales.add(translation.locale);
    }
    return [...locales];
  },
  translationGroups(state: TranslationState): string[] {
    return uniq<string>(
      state.translations.map(translation => translation.group as string)
    );
  },
  translationNamespaces(state: TranslationState): string[] {
    return uniq<string>(state.translations
      .map(translation => translation.namespace)
      .filter(namespace => namespace) as string[]);
  },
  filteredTranslations(state: TranslationState): Translation[] {
    return state.translations.filter(translation => {
      if (
        state.filterNamespace &&
        !translation!
          .namespace!.toLowerCase()
          .includes(state.filterNamespace.toLowerCase())
      ) {
        return false;
      }

      if (
        state.filterGroup &&
        !translation!
          .group!.toLowerCase()
          .includes(state.filterGroup.toLowerCase())
      ) {
        return false;
      }

      if (
        state.filterLocale &&
        !translation!
          .locale!.toLowerCase()
          .includes(state.filterLocale.toLowerCase())
      ) {
        return false;
      }

      if (
        state.filterKey &&
        !translation!.key!.toLowerCase().includes(state.filterKey.toLowerCase())
      ) {
        return false;
      }

      if (
        state.filterValue &&
        !translation!
          .value!.toLowerCase()
          .includes(state.filterValue.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }
};

// actions, to handle async call
const actions = {
  async getTranslations(
    { commit }: ActionContext<TranslationState, RootState>,
    translationFilters: any
  ) {
    const translations: Translation[] = await translationSvc.getTranslations(
      translationFilters
    );
    commit(TRANSLATIONS_UPDATE_TRANSLATIONS, translations);
  },
  async getLocales({ commit }: ActionContext<TranslationState, RootState>) {
    const locales: String[] = await translationSvc.getLocales();
    commit(TRANSLATIONS_UPDATE_LOCALES, locales);
  }
};

const mutations = {
  [TRANSLATIONS_UPDATE_TRANSLATIONS](
    state: TranslationState,
    translations: Translation[]
  ) {
    state.translations = translations;
  },
  [TRANSLATIONS_UPDATE_LOCALES](state: TranslationState, locales: string[]) {
    state.locales = locales;
  },
  [TRANSLATIONS_ADD_TRANSLATIONS](
    state: TranslationState,
    translation: Translation
  ) {
    state.translations.push(translation);
  },
  [TRANSLATIONS_UPDATE_FILTER_GROUP](state: TranslationState, group: string) {
    state.filterGroup = group;
  },
  [TRANSLATIONS_UPDATE_FILTER_KEY](state: TranslationState, key: string) {
    state.filterKey = key;
  },
  [TRANSLATIONS_UPDATE_FILTER_LOCALE](state: TranslationState, locale: string) {
    state.filterLocale = locale;
  },
  [TRANSLATIONS_UPDATE_FILTER_NAMESPACE](
    state: TranslationState,
    namespace: string
  ) {
    state.filterNamespace = namespace;
  },
  [TRANSLATIONS_UPDATE_FILTER_VALUE](state: TranslationState, value: string) {
    state.filterValue = value;
  }
};

export default {
  state: modState,
  getters,
  actions,
  mutations
};
